import { isUndefined } from 'lodash';

import fetch from 'utilities/fetch';
import { paths } from 'config/path_helpers';
import { PurchaseAgreement } from 'types/purchase_agreement';

export const analyticsSaveRecentFilters = (filters) => {
  fetch(paths.api.statisticRecentFilters(), {
    method: 'POST',
    body: JSON.stringify({ filters })
  });
};

export const createGradingRequestResponseRetry = (responseId: number, audioFile: Blob) => {
  return fetch(paths.api.aiGradingRequestResponseRetries(responseId), {
    method: 'POST',
    body: JSON.stringify({ responseRetry: { audioFile } })
  }).then((response) => {
    return response.responseRetry;
  });
};

export const fetchAnswers = (filters, page, studentId?: number) => {
  return fetch(paths.api.answers(filters, page, studentId)).then((response) => {
    return response.answers;
  });
};

export const fetchAnswersSummary = (filters, studentId?: number) => {
  return fetch(paths.api.answersSummary(filters, studentId)).then((response) => {
    return response.answers.summary;
  });
};

export const fetchGradingRequestResponseRetry = (responseId: number, retryId: number) => {
  return fetch(paths.api.aiGradingRequestResponseRetry(responseId, retryId)).then((response) => {
    return response.responseRetry;
  });
};

export const fetchPage = (path) => {
  const url = new URL(path, window.location.origin);
  const params = url.search;
  const jsonPath = `${url.pathname}.json${params}`;

  return fetch(jsonPath).then((response) => {
    return response;
  });
};

export const fetchPracticeSessions = (page, studentId?: number) => {
  return fetch(paths.api.practiceSessions(page, studentId)).then((response) => {
    return response.practiceSessions;
  });
};

export const fetchStatistic = (name, filters) => {
  return fetch(paths.api.statistic(name, filters)).then((response) => {
    return response.statistic;
  });
};

export const cancelPurchaseAgreement = (purchaseAgreement: PurchaseAgreement) => {
  return fetch(paths.api.purchaseAgreementCancel(purchaseAgreement.id), {
    method: 'PUT'
  }).then((response) => response.purchaseAgreement);
};

export const fetchUserProfile = () => {
  return fetch(paths.api.userProfile()).then((response) => {
    return response.userProfile;
  });
};

export const finishMockTest = (id: number) => {
  return fetch(paths.api.mockTestFinish(id), {
    method: 'POST'
  }).then((response) => {
    return response.mockTest;
  });
};

export const pausePurchaseAgreement = (purchaseAgreement: PurchaseAgreement) => {
  const path = purchaseAgreement.id
    ? paths.api.purchaseAgreementPause(purchaseAgreement.id)
    : paths.api.subscriptionPause(purchaseAgreement.legacySubscriptionId);

  return fetch(path, { method: 'PUT' }).then((response) => response.purchaseAgreement);
};

export const unpausePurchaseAgreement = (purchaseAgreement: PurchaseAgreement) => {
  const path = purchaseAgreement.id
    ? paths.api.purchaseAgreementResume(purchaseAgreement.id)
    : paths.api.subscriptionUnpause(purchaseAgreement.legacySubscriptionId);

  return fetch(path, { method: 'PUT' }).then((response) => response.purchaseAgreement);
};

export const regradeGradingRequestResponseRetry = (responseId: number, retryId: number) => {
  return fetch(paths.api.aiGradingRequestResponseRetryRegrade(responseId, retryId), {
    method: 'POST'
  }).then((response) => {
    return response.responseRetry;
  });
};

// Answers either the saved card or the challenge the student's bank wants answered first, which
// they confirm in the browser before calling again with the setup intent
export const updatePurchaseAgreementPaymentMethod = (
  purchaseAgreement: PurchaseAgreement,
  stripePaymentMethodId: string,
  setupIntentId?: string
) => {
  const path = purchaseAgreement.id
    ? paths.api.purchaseAgreementPaymentMethod(purchaseAgreement.id)
    : paths.api.subscriptionPaymentMethod(purchaseAgreement.legacySubscriptionId);

  return fetch(path, {
    method: 'PUT',
    body: JSON.stringify({
      // Where a bank that takes the student off site should return them once the card is saved
      nextUrl: typeof window === 'undefined' ? null : window.location.pathname,
      setupIntentId,
      stripePaymentMethodId
    })
  });
};

export const updateUserProfile = (userProfile) => {
  return fetch(paths.api.userProfile(), {
    method: 'PUT',
    body: JSON.stringify({ userProfile })
  }).then((response) => {
    return response.userProfile;
  });
};
